import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Calendar as CalendarIcon,
  Trash2,
  Edit2,
  CheckCircle,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { ContentTag } from "@/components/ui/content-tag";
import { getTemplateInfo, getLengthTagInfo } from "@/lib/contentHelpers";
import { Card, CardContent } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";

export type CalendarItem = {
  id: string;
  title: string;
  digest?: string; // Short summary/description
  fullContent?: string; // Full article content
  isGenerating?: boolean; // Loading state for background writing
  isGenerated?: boolean; // Track if item has been generated
  postThemeId?: string; // Database ID for post_themes table
  // Optional metadata for generation
  format_template?: string;
  length_tag?: string;
  tone_style?: any;
  keywords?: string[];
  section_layout?: string[];
  element_settings?: Record<string, any>;
  content_type?: string;
  scheduled_at?: string | null;
};

type CalendarDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  items: CalendarItem[];
  onRemove: (id: string) => void;
  onEdit?: (item: CalendarItem) => void;
  onDateChange?: (itemId: string, date: Date) => void;
};

export default function CalendarDrawer({
  isOpen,
  onClose,
  items,
  onRemove,
  onEdit,
  onDateChange,
}: CalendarDrawerProps) {
  const hasItems = items.length > 0;
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);

  // Persist flag so the sidebar can reveal the Calendar menu when first item is added
  useEffect(() => {
    if (hasItems && localStorage.getItem("calendar_enabled") !== "1") {
      localStorage.setItem("calendar_enabled", "1");
      window.dispatchEvent(new Event("calendar-enabled-changed"));
    }
  }, [hasItems]);

  // Group items by month
  const itemsByMonth = useMemo(() => {
    const grouped: { [key: string]: CalendarItem[] } = {};
    
    items.forEach(item => {
      if (item.scheduled_at) {
        const monthKey = format(new Date(item.scheduled_at), 'yyyy-MM');
        if (!grouped[monthKey]) {
          grouped[monthKey] = [];
        }
        grouped[monthKey].push(item);
      } else {
        // Handle unscheduled items
        if (!grouped['unscheduled']) {
          grouped['unscheduled'] = [];
        }
        grouped['unscheduled'].push(item);
      }
    });

    // Sort items within each month by date (newest first)
    Object.keys(grouped).forEach(monthKey => {
      grouped[monthKey].sort((a, b) => {
        const ta = a.scheduled_at ? Date.parse(a.scheduled_at) : 0;
        const tb = b.scheduled_at ? Date.parse(b.scheduled_at) : 0;
        return tb - ta;
      });
    });

    return grouped;
  }, [items]);

  // Get filtered items based on selected month
  const filteredItems = useMemo(() => {
    if (!selectedMonth) {
      return items.sort((a, b) => {
        const ta = a.scheduled_at ? Date.parse(a.scheduled_at) : 0;
        const tb = b.scheduled_at ? Date.parse(b.scheduled_at) : 0;
        return tb - ta;
      });
    }
    return itemsByMonth[selectedMonth] || [];
  }, [items, itemsByMonth, selectedMonth]);

  // Get available months
  const availableMonths = useMemo(() => {
    return Object.keys(itemsByMonth).filter(key => key !== 'unscheduled').sort().reverse();
  }, [itemsByMonth]);

  const handleDateSelect = (item: CalendarItem, date: Date | undefined) => {
    if (date) {
      if (onDateChange) {
        // Use onDateChange for database items
        onDateChange(item.id, date);
      } else if (onEdit) {
        // Fallback to onEdit for localStorage items
        const updatedItem = { ...item, scheduled_at: date.toISOString() };
        onEdit(updatedItem);
      }
    }
  };

  const formatMonthDisplay = (monthKey: string) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return format(date, 'MMM yyyy');
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />
      )}
      <div
        className={cn(
          "fixed right-0 top-0 h-full w-[95%] max-w-6xl bg-background border-l shadow-lg transform transition-transform duration-300 ease-in-out z-50",
          isOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold">Planned Content</h2>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Month Filter */}
          <div className="p-4 border-b border-border bg-muted/20">
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant={selectedMonth === null ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setSelectedMonth(null)}
                className="h-8"
              >
                All posts
              </Button>
              <div className="flex items-center gap-1">
                {selectedMonth && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => {
                      const idx = availableMonths.indexOf(selectedMonth);
                      const next = idx >= 0 ? availableMonths[idx + 1] : undefined;
                      if (next) setSelectedMonth(next);
                    }}
                    disabled={(() => {
                      if (!selectedMonth) return true;
                      const idx = availableMonths.indexOf(selectedMonth);
                      return idx < 0 || idx + 1 >= availableMonths.length;
                    })()}
                    title="Previous month"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                )}
                <Select
                  value={selectedMonth ?? undefined}
                  onValueChange={(v) => setSelectedMonth(v)}
                >
                  <SelectTrigger className="h-8 w-[180px] bg-background border-border hover:bg-accent hover:text-accent-foreground">
                    <SelectValue placeholder="Select month" />
                  </SelectTrigger>
                  <SelectContent className="bg-background">
                    {availableMonths.map((monthKey) => (
                      <SelectItem key={monthKey} value={monthKey}>
                        {formatMonthDisplay(monthKey)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedMonth && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => {
                      const idx = availableMonths.indexOf(selectedMonth);
                      const prev = idx > 0 ? availableMonths[idx - 1] : undefined;
                      if (prev) setSelectedMonth(prev);
                    }}
                    disabled={(() => {
                      if (!selectedMonth) return true;
                      const idx = availableMonths.indexOf(selectedMonth);
                      return idx <= 0;
                    })()}
                    title="Next month"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
          {/* Content */}
          <ScrollArea className="flex-1">
            <div className="p-4">
              {filteredItems.length === 0 ? (
                <div className="text-sm text-muted-foreground text-center py-8">
                  {selectedMonth ? `No items for ${formatMonthDisplay(selectedMonth)}` : 'No items yet. Add an idea from chat to see it here.'}
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredItems.map((item) => (
              <Card 
                key={item.id} 
                className="cursor-pointer hover:shadow-md transition-shadow border border-gray-200"
                onClick={() => onEdit?.(item)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className={cn(
                                "justify-start text-left font-normal h-8 px-2",
                                !item.scheduled_at && "text-muted-foreground"
                              )}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {item.scheduled_at ? format(new Date(item.scheduled_at), 'PPP') : <span>Pick a date</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={item.scheduled_at ? new Date(item.scheduled_at) : undefined}
                              onSelect={(date) => handleDateSelect(item, date)}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        {item.isGenerated && item.fullContent && (
                          <div className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="h-3 w-3" />
                            <span className="text-xs font-medium">Ready</span>
                          </div>
                        )}
                      </div>
                      
                      <h3 className="text-sm font-medium leading-snug mb-2">{item.title}</h3>
                      
                      {item.isGenerating && (
                        <div className="flex items-center gap-2 mb-2">
                          <div className="animate-spin h-3 w-3 border border-blue-600 border-t-transparent rounded-full"></div>
                          <p className="text-xs text-blue-700">Writing article in background...</p>
                        </div>
                      )}
                      
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {item.format_template && (
                          <ContentTag
                            type="category"
                            label={item.format_template}
                            description={getTemplateInfo(item.format_template).description}
                          />
                        )}
                        {item.length_tag && (
                          <ContentTag
                            type="length"
                            label={item.length_tag}
                            description={getLengthTagInfo(item.length_tag).description}
                          />
                        )}
                        {item.content_type && item.content_type !== 'post' && (
                          <ContentTag
                            type="category"
                            label={item.content_type}
                          />
                        )}
                        {item.keywords && item.keywords.length > 0 && (
                          <ContentTag
                            type="keyword"
                            label={item.keywords.slice(0, 2).join(', ')}
                            description={item.keywords.length > 2 ? `+${item.keywords.length - 2} more` : undefined}
                          />
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-1.5 ml-4">
                      {onEdit && (
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(item)} title="Edit">
                          <Edit2 className="h-4 w-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onRemove(item.id)} title="Remove">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
                  ))}
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      </div>
    </>
  );
}
